import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PageSEO from '@/components/PageSEO';
import { 
  ArrowLeft, 
  Brain, 
  Lightbulb, 
  TrendingUp, 
  Search, 
  Zap,
  Building2,
  BookOpen,
  Award,
  ShieldCheck
} from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <PageSEO
        title="About Evidentica | Research Intelligence Platform"
        description="Learn about Evidentica's mission to advance medical research through AI-powered analysis, scoring, and evidence evaluation."
        canonical="https://evidentica.net/about"
      />
      {/* Header */}
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Brain className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-slate-800 dark:text-white">Evidentica</span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">About Evidentica</h1>
          <p className="text-lg text-blue-100 max-w-3xl mx-auto">
            AI-Powered Medical Research Intelligence Platform by International Sci Ink Press Ltd
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        
        {/* Company Information */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl text-blue-900">
              <Building2 className="mr-3 h-6 w-6" />
              About International Sci Ink Press Ltd
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-base text-slate-700">
              <strong>Evidentica</strong> is a flagship platform developed and operated by <strong>International Sci Ink Press Ltd</strong>, 
              a leading medical research and publishing organization dedicated to advancing evidence-based medicine through innovative technology.
            </p>
            <p className="text-slate-700">
              Founded by <strong>Dr. Ivan Inkov</strong>, International Sci Ink Press Ltd specializes in medical research publication, 
              systematic evidence analysis, and AI-powered research intelligence tools.
            </p>
          </CardContent>
        </Card>

        {/* Partner Journals */}
        <Card className="border-2 border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl text-purple-900">
              <BookOpen className="mr-3 h-6 w-6" />
              Partner Journals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-700 mb-4">
              All research articles analyzed on Evidentica are published in our prestigious peer-reviewed medical journals:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border-2 border-purple-300 shadow-sm">
                <h3 className="text-lg font-bold text-purple-900 mb-2">
                  International Journal of Surgery and Medicine
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Peer-reviewed journal focusing on surgical innovations, clinical outcomes, and evidence-based surgical practice.
                </p>
                <div className="space-y-1">
                  <Badge className="bg-purple-600">E-ISSN: 2367-699X</Badge>
                  <Badge className="bg-purple-600 ml-2">P-ISSN: 2367-7414</Badge>
                  <p className="text-sm text-slate-600 mt-2">
                    <a href="https://www.ejos.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      https://www.ejos.org/
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border-2 border-purple-300 shadow-sm">
                <h3 className="text-lg font-bold text-purple-900 mb-2">
                  International Journal of Medical Reviews and Case Reports
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Publishing systematic reviews, meta-analyses, and significant clinical case reports across all medical specialties.
                </p>
                <div className="space-y-1">
                  <Badge className="bg-purple-600">ISSN: 2534-9821</Badge>
                  <p className="text-sm text-slate-600 mt-2">
                    <a href="https://www.mdpub.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      https://www.mdpub.net/
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Platform Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">What is Evidentica?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-base text-slate-700">
              Evidentica is an <strong>AI-powered medical research intelligence platform</strong> that transforms how researchers, 
              clinicians, and medical professionals discover, evaluate, and utilize evidence-based research.
            </p>
            <p className="text-slate-700">
              Using advanced artificial intelligence (GPT-5.2), Evidentica automatically analyzes research articles to provide 
              comprehensive methodological assessments, evidence quality ratings, and actionable research insights.
              The platform employs a fully <strong>deterministic scoring system</strong> for methodology evaluation, ensuring
              transparent and reproducible quality assessments.
            </p>
          </CardContent>
        </Card>

        {/* Key Features */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center">
              <Zap className="mr-2 h-6 w-6 text-yellow-500" />
              Key Features & Innovations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Deterministic Methodology Scoring */}
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <Brain className="mr-2 h-5 w-5 text-green-600" />
                  Deterministic Methodology Scoring
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  For primary clinical research (RCTs, cohort studies, case-control, cross-sectional): transparent, rule-based 
                  assessment using Study Design, Sample Size, Follow-up Duration, and Methodological Rigor. Max score: 70 points.
                </p>
                <Badge variant="outline" className="mt-2">Fully Deterministic</Badge>
              </div>

              {/* Evidence Synthesis Score */}
              <div className="border-l-4 border-teal-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <Search className="mr-2 h-5 w-5 text-teal-600" />
                  Evidence Synthesis Score (ESS)
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  For systematic reviews, meta-analyses, scoping reviews, narrative reviews, and critical appraisals: 
                  Review Design (0-15) + Transparency (0-10) + Synthesis Structure (0-5) + Clinical Relevance (0-5). Max: 35 points.
                </p>
                <Badge variant="outline" className="mt-2">AI + Deterministic</Badge>
              </div>

              {/* Data Integrity Check */}
              <div className="border-l-4 border-emerald-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <ShieldCheck className="mr-2 h-5 w-5 text-emerald-600" />
                  Data Integrity Assessment
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  AI-powered evaluation of data consistency, reporting transparency, and statistical rigor.
                  Acts as a multiplier on the Methodology Score, rewarding high-integrity research.
                </p>
                <Badge variant="outline" className="mt-2">AI Multiplier</Badge>
              </div>

              {/* Comprehensive Research Analysis */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <Search className="mr-2 h-5 w-5 text-blue-600" />
                  8-Part Research Intelligence
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  In-depth AI analysis covering: Core Scientific Idea, PICO Framework, Methodology Assessment, 
                  Research Gaps, Future Directions, Clinical Translation, Evidence Level, and SEO Optimization.
                </p>
                <Badge variant="outline" className="mt-2">GPT-5.2 Powered</Badge>
              </div>

              {/* Case Report Evaluation */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <Lightbulb className="mr-2 h-5 w-5 text-purple-600" />
                  Clinical Learning Value (Case Reports)
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  Specialized scoring for case reports using a Clinical Learning Value assessment (0-10 scale), 
                  converted to an Evidence Score via a x3.5 multiplier (max 35 points).
                </p>
                <Badge variant="outline" className="mt-2">Specialized Scoring</Badge>
              </div>

              {/* SEO & Discovery */}
              <div className="border-l-4 border-amber-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <TrendingUp className="mr-2 h-5 w-5 text-amber-600" />
                  SEO & Discovery Optimization
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  Automatic generation of search-optimized metadata, schema.org structured data, 
                  social media previews, and discovery tags for maximum research visibility.
                </p>
                <Badge variant="outline" className="mt-2">7 SEO modules</Badge>
              </div>

              {/* Citation Generator */}
              <div className="border-l-4 border-rose-500 pl-4">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                  <Award className="mr-2 h-5 w-5 text-rose-600" />
                  Multi-Format Citations
                </h3>
                <p className="text-sm text-slate-600 mb-2">
                  AI-generated accurate citations in APA 7th, MLA 9th, Chicago 17th, and Vancouver formats 
                  based on article metadata.
                </p>
                <Badge variant="outline" className="mt-2">4 formats</Badge>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Scoring System Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Impact Index Calculation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 p-6 rounded-lg border-2 border-slate-200">
              <p className="text-base font-mono text-center mb-2">
                Impact Index = <span className="text-blue-600 font-bold">Evidence Score</span> + <span className="text-purple-600 font-bold">Visibility Score (0-30)</span>
              </p>
              <p className="text-sm text-slate-500 text-center mb-6">
                The Evidence Score depends on the article type (see below)
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                {/* Primary Clinical Research */}
                <div className="bg-white p-4 rounded-lg border-2 border-green-200">
                  <h4 className="font-bold text-green-900 mb-3">Methodology Score (Max 70)</h4>
                  <p className="text-xs text-slate-500 mb-2">For: RCT, cohort, case-control, cross-sectional</p>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>Study Design (0-30)</li>
                    <li>Sample Size (0-15)</li>
                    <li>Follow-up Duration (0-15)</li>
                    <li>Methodological Rigor (0-10)</li>
                    <li className="text-emerald-600 font-semibold mt-2">x Data Integrity Multiplier (0.5-1.15)</li>
                  </ul>
                </div>

                {/* Evidence Synthesis */}
                <div className="bg-white p-4 rounded-lg border-2 border-teal-200">
                  <h4 className="font-bold text-teal-900 mb-3">Evidence Synthesis Score (Max 35)</h4>
                  <p className="text-xs text-slate-500 mb-2">For: systematic reviews, meta-analyses, scoping reviews, narrative reviews, critical appraisals</p>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>Review Design (0-15) — deterministic</li>
                    <li>Transparency (0-10) — AI-evaluated</li>
                    <li>Synthesis Structure (0-5)</li>
                    <li>Clinical Relevance (0-5)</li>
                  </ul>
                </div>

                {/* Case Reports */}
                <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
                  <h4 className="font-bold text-purple-900 mb-3">Clinical Value Score (Max 35)</h4>
                  <p className="text-xs text-slate-500 mb-2">For: case reports, case series</p>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>Documentation Quality (0-3)</li>
                    <li>Novelty / Rarity (0-3)</li>
                    <li>Educational Value (0-2)</li>
                    <li>Follow-up & Outcome (0-2)</li>
                    <li className="text-purple-600 font-semibold mt-2">Total (0-10) x 3.5 = Score</li>
                  </ul>
                </div>
              </div>

              {/* Visibility */}
              <div className="mt-6 bg-white p-4 rounded-lg border-2 border-purple-200">
                <h4 className="font-bold text-purple-900 mb-3">Visibility Score (Max 30) — All Article Types</h4>
                <ul className="space-y-1 text-sm text-slate-700 grid md:grid-cols-3 gap-2">
                  <li>PDF Downloads (max 8 pts)</li>
                  <li>Citations (max 10 pts)</li>
                  <li>Altmetric Score (max 7 pts)</li>
                  <li>PlumX Metrics (max 3 pts)</li>
                  <li>Recency Bonus (max 2 pts)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score History */}
        <Card className="border-2 border-indigo-200 bg-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl text-indigo-900">
              <TrendingUp className="mr-3 h-6 w-6" />
              Score History & Transparency
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-700">
              Every change to an article's Impact Index is tracked and displayed in a <strong>Score History</strong> timeline.
              Authors and readers can see exactly when and why scores changed, whether from initial analysis, 
              re-analysis with PDF full text, or manual recalculation. This ensures full transparency and 
              builds trust in the scoring system.
            </p>
          </CardContent>
        </Card>

        {/* Footer CTA */}
        <div className="text-center py-8">
          <Button 
            size="lg" 
            onClick={() => navigate('/explorer')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            data-testid="explore-articles-btn"
          >
            Explore Research Articles
          </Button>
        </div>

      </div>
    </div>
  );
};

export default AboutPage;
